import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { computeSegmentWarnings } from "../../pages/ScoreOverView/utils/computeSegmentWarnings";
import { ScoreCriteriaType } from "../../schema/score.schema";
import { FocusTarget, focusMatches } from "../focusTarget";
import { InlineName } from "../InlineName";
import {
	CellAssignment,
	cellKey,
	PersonaView,
	SegmentGroup,
	SegmentView,
} from "../scoreConfigView";

import { MatrixCell, MatrixCellActions } from "./MatrixCell";
import { bandsOf, scoringOptions } from "./matrixModel";

interface ScoreMatrixProps extends MatrixCellActions {
	segments: SegmentView[];
	personas: PersonaView[];
	criteria: ScoreCriteriaType[];
	isLinked: (segment: SegmentView, persona: PersonaView) => boolean;
	assignmentOf: (segment: SegmentView, persona: PersonaView) => CellAssignment | null;
	onRename: (kind: "segment" | "persona", row: SegmentView | PersonaView, name: string) => void;
	/** Right-aligned control in the corner cell (the "Add" menu). */
	corner?: React.ReactNode;
	focusTarget?: FocusTarget | null;
	onFocusDone?: () => void;
	/** Rows and columns switched off are hidden; the pages show them in their own lists. */
	className?: string;
}

const BAND_BAR: Record<SegmentGroup, string> = {
	PROSPECT: "bg-brand-light",
	PARTNER: "bg-rb-turquoise",
	INVESTOR: "bg-status-warning",
};

const BAND_LABEL: Record<SegmentGroup, string> = {
	PROSPECT: "prospects",
	PARTNER: "partners",
	INVESTOR: "investors",
};

const FIRST_COLUMN = "sticky left-0 z-10 min-w-[248px] w-[248px] bg-white";

/**
 * The segment × persona matrix (PREC-4055), shared by the Overview and the
 * Lead Scoring page. Rows are banded by group and collapsible; the header row
 * and the first column stay put while the table scrolls.
 */
export const ScoreMatrix: FunctionComponent<ScoreMatrixProps> = ({
	segments,
	personas,
	criteria,
	isLinked,
	assignmentOf,
	onRename,
	corner,
	focusTarget = null,
	onFocusDone = () => undefined,
	className,
	...cellActions
}) => {
	const { t } = useTranslation();
	const [closed, setClosed] = useState<Set<SegmentGroup>>(new Set());

	const liveSegments = segments.filter((segment) => segment.active);
	const livePersonas = personas.filter((persona) => persona.active);
	const bands = bandsOf(liveSegments);
	const options = useMemo(() => scoringOptions(criteria), [criteria]);

	const { multipleActiveCells } = useMemo(
		() =>
			computeSegmentWarnings(
				criteria,
				(companyId, personaId) => {
					const segment = liveSegments.find((row) => row.id === companyId);
					const persona = livePersonas.find((row) => row.id === personaId);

					return !!segment && !!persona && isLinked(segment, persona);
				},
				(personaId) => livePersonas.find((row) => row.id === personaId)?.investors ?? false
			),
		[criteria, liveSegments, livePersonas, isLinked]
	);

	const toggleBand = (group: SegmentGroup) =>
		setClosed((current) => {
			const next = new Set(current);

			next.has(group) ? next.delete(group) : next.add(group);

			return next;
		});

	return (
		<div className={classNames("overflow-hidden rounded-xl border border-border-light", className)}>
			<div className="max-h-[calc(100vh-300px)] overflow-auto">
				<table
					className="w-full border-separate border-spacing-0 text-xs"
					data-testid="score-matrix"
				>
					<thead>
						<tr>
							<th
								className={classNames(
									FIRST_COLUMN,
									"sticky top-0 z-30 border-b border-r border-border-light bg-background-light px-3 py-2 text-left font-semibold text-text-meta"
								)}
							>
								<div className="flex items-center gap-2">
									<span>{t("leadScoring.matrix.segments")}</span>
									<span className="ml-auto">{corner}</span>
								</div>
							</th>
							{livePersonas.map((persona) => (
								<th
									key={persona.id}
									className="sticky top-0 z-20 min-w-[200px] border-b border-r border-border-light bg-background-light px-3 py-2 text-left font-semibold text-text-meta last:border-r-0"
								>
									<InlineName
										testId={`persona-name-${persona.id}`}
										value={persona.name}
										onChange={(name) => onRename("persona", persona, name)}
									/>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{bands.map((band) => (
							<BandRows
								key={band.group}
								assignmentOf={assignmentOf}
								band={band}
								closed={closed.has(band.group)}
								columns={livePersonas.length}
								focusTarget={focusTarget}
								isLinked={isLinked}
								multipleActiveCells={multipleActiveCells}
								options={options}
								personas={livePersonas}
								onFocusDone={onFocusDone}
								onRename={onRename}
								onToggle={() => toggleBand(band.group)}
								{...cellActions}
							/>
						))}
						{liveSegments.length === 0 && (
							<tr>
								<td
									className="px-4 py-8 text-center text-sm text-text-disabled"
									colSpan={livePersonas.length + 1}
								>
									{t("leadScoring.noCompanySegmentsConfigured")}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
};

interface BandRowsProps extends MatrixCellActions {
	band: ReturnType<typeof bandsOf>[number];
	closed: boolean;
	columns: number;
	personas: PersonaView[];
	options: ReturnType<typeof scoringOptions>;
	isLinked: ScoreMatrixProps["isLinked"];
	assignmentOf: ScoreMatrixProps["assignmentOf"];
	onRename: ScoreMatrixProps["onRename"];
	multipleActiveCells: Set<string>;
	focusTarget: FocusTarget | null;
	onFocusDone: () => void;
	onToggle: () => void;
}

const BandRows: FunctionComponent<BandRowsProps> = ({
	band,
	closed,
	columns,
	personas,
	options,
	isLinked,
	assignmentOf,
	onRename,
	multipleActiveCells,
	focusTarget,
	onFocusDone,
	onToggle,
	...cellActions
}) => {
	const { t } = useTranslation();

	return (
		<>
			<tr>
				<th
					className={classNames(
						FIRST_COLUMN,
						"sticky top-[33px] z-20 border-b border-t-2 border-border-light p-0"
					)}
				>
					<button
						className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-text-meta"
						data-testid={`matrix-band-${band.group}`}
						type="button"
						onClick={onToggle}
					>
						<span className={classNames("h-2.5 w-0.5 rounded", BAND_BAR[band.group])} />
						{t(`leadScoring.matrix.${BAND_LABEL[band.group]}`)}
						<span className="font-medium text-text-disabled">{band.segments.length}</span>
						<Icon
							className={classNames(
								"ml-auto h-3 w-3 text-text-disabled transition-transform",
								closed && "-rotate-90"
							)}
							icon={faChevronDown}
						/>
					</button>
				</th>
				<th
					className="sticky top-[33px] z-10 border-b border-t-2 border-border-light bg-white"
					colSpan={columns}
				/>
			</tr>
			{!closed &&
				band.segments.map((segment) => (
					<tr key={segment.key} className="hover:bg-selected-item">
						<td
							className={classNames(
								FIRST_COLUMN,
								"border-b border-r border-border-light px-3 py-2 pl-6 font-semibold text-text"
							)}
						>
							<InlineName
								testId={`segment-name-${segment.key}`}
								value={segment.name}
								onChange={(name) => onRename("segment", segment, name)}
							/>
						</td>
						{personas.map((persona) => {
							const key = cellKey(segment, persona.id);

							return (
								<td
									key={persona.id}
									className="isolate border-b border-r border-border-light px-3 py-1.5 last:border-r-0"
								>
									<MatrixCell
										ambiguous={multipleActiveCells.has(key)}
										assignment={assignmentOf(segment, persona)}
										flash={focusMatches(focusTarget, "cell", segment.key, persona.id)}
										linked={isLinked(segment, persona)}
										options={options}
										persona={persona}
										segment={segment}
										onFlashDone={onFocusDone}
										{...cellActions}
									/>
								</td>
							);
						})}
					</tr>
				))}
		</>
	);
};
